import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserAvatar } from '@/components/UserAvatar'
import { type User } from '@/features/users/types/user'
import { useTranslation } from 'react-i18next'

type UserSelectProps = {
  users: User[]
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  testId?: string
}

function getUserDisplayName(user: User) {
  return user.name || user.username || user.id
}

function UserOption({ user }: { user: User }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <UserAvatar
        name={user.name}
        username={user.username}
        avatarId={user.avatarId}
        className="size-7 rounded-md border border-border/40"
        fallbackClassName="rounded-md text-[10px] font-medium text-muted-foreground"
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-xs font-medium text-foreground">{getUserDisplayName(user)}</span>
        <span className="truncate text-[10px] text-muted-foreground">{user.username || user.role}</span>
      </div>
    </div>
  )
}

export function UserSelect({ users, value, onValueChange, disabled, placeholder, testId }: UserSelectProps) {
  const { t } = useTranslation()
  const selectedUser = users.find(user => user.id === value)

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger data-testid={testId ?? 'user-select-trigger'} className="h-9 w-full bg-secondary/50 text-xs">
        {selectedUser ? (
          <UserOption user={selectedUser} />
        ) : (
          <SelectValue placeholder={placeholder ?? t('users.selectUser')} />
        )}
      </SelectTrigger>
      <SelectContent className="bg-card text-foreground">
        {users.map(user => (
          <SelectItem
            data-testid={`user-select-option-${user.username}`}
            key={user.id}
            value={user.id}
            textValue={`${getUserDisplayName(user)} ${user.username ?? ''}`}
          >
            <UserOption user={user} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
